import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {ProminentBannerSection_CouponSlider} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {FadeTransition} from '@aboutyou/transitions';
import {
  COLORS,
  Countdown,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  IconName,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {getCouponExpiryDate} from '../../../../../features/Coupons/getCouponExpiryDate';
import {getCouponValues} from '../../../../../features/Coupons/utils/getCouponValues';
import {useIsUrgencyTriggersDisabled} from '../../../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {CustomCountdownUnit} from './CustomCountdownUnit';

export const CouponSliderHeader: React.FC<{
  activatedCoupon?: Coupon | null;
  couponSlider: ProminentBannerSection_CouponSlider;
  flatBorders?: boolean;
  isActive: boolean;
  isOpen?: boolean;
  headline?: string;
  fullWidthHeadline?: boolean;
}> = ({
  activatedCoupon,
  couponSlider,
  flatBorders,
  isActive,
  isOpen,
  headline,
  fullWidthHeadline,
}) => {
  const {t} = useTranslation();

  const headerData = (() => {
    if (isActive) {
      return {
        text: isOpen ? t('Close') : t('Active'),
        icon: isOpen
          ? GenericIconName.AlertErrorCircleFill
          : GenericIconName.CheckmarkCircleWhiteCheck,
        color: isActive
          ? couponSlider.backgroundColor.hex
          : couponSlider.foregroundColor.hex,
        backgroundColor: 'rgba(21, 57, 207, 0.7)',
      };
    }

    return {
      text: isOpen ? t('Close') : t('Add Coupon'),
      icon: isOpen
        ? GenericIconName.AlertErrorCircleFill
        : GenericIconName.AddCircleFill,
      color: couponSlider.foregroundColor.hex,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    };
  })();

  const expirationDate = getCouponExpiryDate(activatedCoupon);

  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();
  const discount = getCouponValues(activatedCoupon).discount;

  return (
    <Wrapper
      $backgroundColor={headerData.backgroundColor}
      $flatBorders={flatBorders}
    >
      <FadeTransition duration={500}>
        <Content $color={headerData.color}>
          {headline ? (
            <Headline
              data-testid="coupon-slider-header"
              $fullWidthHeadline={fullWidthHeadline}
            >
              {headline}
            </Headline>
          ) : (
            <CountdownWrapper>
              {disableUrgencyTriggers && discount ? (
                <DiscountValue>
                  {discount.value}
                  {discount.unit} {t('coupon')}
                </DiscountValue>
              ) : (
                expirationDate && (
                  <>
                    <CountdownIcon
                      asset={GenericIconName.Clock}
                      size="XS"
                      fill={COLORS.White}
                    />
                    <StyledCountdown
                      size="small"
                      endDate={expirationDate}
                      unitComponent={CustomCountdownUnit}
                    />
                  </>
                )
              )}
            </CountdownWrapper>
          )}

          {!fullWidthHeadline && (
            <Action data-testid="couponSliderHeaderState">
              {headerData.text}
              <Icon
                asset={headerData.icon as IconName}
                size="XS"
                fill={COLORS.PrimaryCoupon}
              />
            </Action>
          )}
        </Content>
      </FadeTransition>
    </Wrapper>
  );
};

const Wrapper = styled.div<{$backgroundColor: string; $flatBorders?: boolean}>`
  height: 34px;
  background-color: ${props => props.$backgroundColor};
  backdrop-filter: blur(6px);
  transition: background-color 0.3s ease-in-out;
  cursor: pointer;

  ${({$flatBorders}) =>
    $flatBorders
      ? css`
          border-top: 1px solid rgba(21, 57, 207, 0.4);
          border-bottom: 1px solid rgba(21, 57, 207, 0.4);
          border-radius: 0px;
        `
      : css`
          border-bottom: 1px solid ${COLORS.Grey3};
          border-radius: 16px 16px 0px 0px;
        `}
`;

const Content = styled.div<{$color: string}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 34px;
  padding: 8px 16px;
  color: ${props => props.$color};
`;

const Headline = styled.span<{$fullWidthHeadline?: boolean}>`
  letter-spacing: 0.48px;
  line-height: 1;
  font-size: ${FONT_SIZES.px12};

  ${({$fullWidthHeadline}) =>
    $fullWidthHeadline &&
    css`
      width: 100%;
      text-align: center;
    `}
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledCountdown = styled(Countdown)`
  padding: 0;
  border: none;
  gap: 4px;
`;

const CountdownIcon = styled(Icon)`
  position: relative;
  top: 1px;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZES.px10};
  gap: 4px;
  line-height: 1.6;
`;

const DiscountValue = styled.span`
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  font-size: ${FONT_SIZES.px12};
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;
